/**
 * FieldPickerOptions (Unit 5 Phase 2) — THE shared field picker.
 *
 * Extracted verbatim from FieldSortModal's inline `<select>` body (Phase 1,
 * e7516b1:FieldSortModal.jsx:65-70 and :157-175) so Sort, Filter and — when it
 * lands — Group render ONE list from ONE definition. Three modals each with
 * their own visible/hidden split is three places for the grouping rule, the
 * separator and the escape hatch to drift, and the Phase 1 device pass already
 * found one ordering fault in the single copy.
 *
 * ORDER — three bands, in this order:
 *   1. PINNED         supplier_tool_id, always first (see THE TOOL ID PIN)
 *   2. VISIBLE        every other field NOT in the hidden Set
 *   3. HIDDEN         behind a disabled "── Hidden fields ──" separator
 *
 * Bands 2 and 3 keep the caller's incoming order — `fields` arrives from the
 * host's view-scoped columnsFields memo, and the picker deliberately does not
 * re-sort it, so the option list matches the Columns modal's row order.
 *
 * THE TOOL ID PIN, and what it fixes. Phase 1 shipped the two-way split with a
 * correct predicate and a WRONG RESULT for one field: `columnsFields` is built
 * as [...editable, ...readonly] (FieldZonePlaceholder.jsx:229) and TOOL ID is
 * the first entry of READONLY_DISPLAY_FIELDS, so it landed at the END of the
 * editables — position 21 of 21 on INV, dead last in the whole picker. It was
 * never below the separator; it was at the bottom of the group ABOVE it. TOOL
 * ID is the row's identity and the field a rep reaches for first, so it is
 * pinned to the top of the visible band regardless of which band it would
 * otherwise fall into.
 *
 * PINNED_FIRST_KEYS IS NOT NEVER_HIDDEN, deliberately, even though the two
 * hold the same one key today. They answer different questions — "may this
 * column be hidden" versus "does this field lead the picker" — and collapsing
 * them would mean a future second never-hidden key silently jumped the queue
 * here, or a future pinned-but-hideable field could not exist at all.
 */

import React from 'react';

/**
 * Keys that lead the visible band, in this order. See the docblock above for
 * why this is its own set and not NEVER_HIDDEN.
 */
export const PINNED_FIRST_KEYS = ['supplier_tool_id'];

/**
 * Split a field list into the picker's bands.
 *
 * A pinned key is pulled out BEFORE the hidden test, which is what makes the
 * "regardless of which group it would otherwise land in" guarantee true: even
 * a pinned key that somehow reached the hidden Set still leads the visible
 * band rather than disappearing behind the separator.
 *
 * `exclude` (Unit 5 Phase 3) REMOVES one key from the list outright. It is
 * not a fourth band and not a relocation: the key is gone. Group's Secondary
 * picker uses it so the field already chosen as Primary cannot also be chosen
 * as Secondary, a pair groupRecords would treat as no secondary at all.
 *
 * The exclusion test runs BEFORE the pin, deliberately: a pinned key that is
 * excluded has to disappear, not lead the visible band. That ordering is the
 * whole reason this lives here rather than in a .filter() at each call site.
 *
 * DEFAULT null, and omitting the argument leaves the output byte-identical —
 * which matters because neither existing consumer is being touched in this
 * phase.
 *
 * @returns {{ visible: Array, hiddenFields: Array }}
 */
export function splitPickerFields(fields, hidden, exclude = null) {
  const list = Array.isArray(fields) ? fields : [];
  const hiddenSet = hidden instanceof Set ? hidden : new Set();
  const excluded = typeof exclude === 'string' && exclude !== '' ? exclude : null;

  const pinned = [];
  const visible = [];
  const hiddenFields = [];

  for (const field of list) {
    // BEFORE the pin, deliberately — see the docblock. An excluded key
    // leaves the list entirely rather than being promoted or relocated.
    if (excluded !== null && field.key === excluded) continue;
    if (PINNED_FIRST_KEYS.includes(field.key)) {
      pinned.push(field);
    } else if (hiddenSet.has(field.key)) {
      hiddenFields.push(field);
    } else {
      visible.push(field);
    }
  }

  // Pinned keys in PINNED_FIRST_KEYS order, not in `fields` order, so the
  // declared precedence is what ships when there is ever more than one.
  pinned.sort(
    (a, b) => PINNED_FIRST_KEYS.indexOf(a.key) - PINNED_FIRST_KEYS.indexOf(b.key),
  );

  return { visible: [...pinned, ...visible], hiddenFields };
}

/**
 * The key a "+ Add …" control should seed with: the first offerable field,
 * visible band before hidden. With the pin in place this is TOOL ID whenever
 * the current view offers it, which is also the most likely first choice.
 * Null when there is nothing to offer at all.
 * `exclude` passes straight through, so a seed key is never a key the
 * caller has removed from the list.
 */
export function firstPickerKey(fields, hidden, exclude = null) {
  const { visible, hiddenFields } = splitPickerFields(fields, hidden, exclude);
  return visible[0]?.key ?? hiddenFields[0]?.key ?? null;
}

/**
 * The `<option>` list for a field `<select>`. Renders a fragment, so the
 * caller owns the `<select>` and its value/onChange — this component decides
 * only WHICH options exist and in WHAT order.
 *
 * `value` drives the escape hatch at the end: a stored condition or sort level
 * naming a field the CURRENT view does not offer (an ALL-only field while the
 * rep sits on INV) still renders as its own option, so the `<select>` can
 * never show blank and silently rewrite the rep's saved choice on the next
 * change event. Mirrors BottomSheetSelect.jsx:130.
 *
 * `exclude` (Unit 5 Phase 3) drops one key from the options entirely. The
 * escape hatch is gated on it as well as on the bands: an excluded key that
 * the current view does not offer would otherwise be UNKNOWN, and the hatch
 * would hand it straight back as an option — re-admitting through the back
 * door the very key the caller removed. A caller should not hold an excluded
 * value in the first place (the Group modal clears the collision as it is
 * made), so this guard is the second of two, not the only one.
 */
export default function FieldPickerOptions({ fields, hidden, value, exclude = null }) {
  const excluded = typeof exclude === 'string' && exclude !== '' ? exclude : null;
  const { visible, hiddenFields } = splitPickerFields(fields, hidden, excluded);
  const known = Array.isArray(fields) ? fields.some((f) => f.key === value) : false;

  return (
    <>
      {visible.map((f) => (
        <option key={f.key} value={f.key}>{f.label}</option>
      ))}
      {hiddenFields.length > 0 && (
        /* Bob's separator, verbatim text (buildFieldOptions:9211-9214).
           `disabled` is what makes it a label and not a selectable value. */
        <option disabled>── Hidden fields ──</option>
      )}
      {hiddenFields.map((f) => (
        <option key={f.key} value={f.key}>{f.label}</option>
      ))}
      {value != null && value !== excluded && !known && (
        <option value={value}>{value}</option>
      )}
    </>
  );
}
